import {
  Component,
  ChangeDetectorRef,
  computed,
  signal,
  ViewChild,
  OnInit,
  OnDestroy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, of } from 'rxjs';
import { TableComponent } from '@view/shared/molecules/app-table/app-table.component';
import { PaginatorComponent } from '@view/shared/molecules/paginator/paginator.component';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';
import { TableActionType } from '@view/shared/molecules/app-table/tableActionTypes';
import { ToastComponent } from '@view/shared/molecules/toast/toast.component';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import { TraceabilityPresenter } from '@view/preclinical/presenters/traceability.presenter';
import {
  CaseListItem,
  VersionHistoryEntry,
  TraceabilityMetrics,
  TraceabilityMetricsFilterParams
} from '@model/traceability/traceability.model';
import { ViewExpansionStateService } from '@helpers/state/view-expansion-state.service';
import { VersionHistoryModalComponent } from '../version-history-modal/version-history-modal.component';
import {
  PrioritizationModalComponent,
  PrioritizationResult
} from '../prioritization-modal/prioritization-modal.component';
import {
  SearchControlsComponent,
  SearchEvent,
  SearchFieldOption
} from '@view/shared/molecules/search-controls/search-controls.component';
import {
  ColumnOption,
  FrozenColumnsSidebarComponent
} from '@view/preclinical/molecules/frozen-columns-sidebar/frozen-columns-sidebar.component';
import { FilterSidebarComponent } from '@view/preclinical/molecules/filter-sidebar/filter-sidebar.component';
import { ActiveFiltersComponent } from '@view/shared/molecules/active-filters/active-filters.component';
import {
  ExportConfirmationModalComponent,
  ExportModalState
} from '@view/preclinical/molecules/export-confirmation-modal/export-confirmation-modal.component';
import { FrozenColumnsService } from '@helpers/table/frozen-columns.service';
import { IFilterOptionsQueryPort } from '../../../../../domain/service/preclinical/filter-options-query.port';
import { FilterCriteria } from '@model/filter/filter-options.model';
import {
  MetricsDashboardComponent,
  MetricItem
} from '@view/shared/molecules/metrics-dashboard/metrics-dashboard.component';

/** Search field options for traceability */
const TRACEABILITY_SEARCH_OPTIONS: SearchFieldOption[] = [
  {
    value: 'identificationNumber',
    label: 'Documento',
    inputType: 'text',
    placeholder: 'Ingrese número de documento'
  },
  {
    value: 'lgNumber',
    label: 'Código LG',
    inputType: 'text',
    placeholder: 'Ingrese código LG'
  }
];

@Component({
  selector: 'app-traceability-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    PaginatorComponent,
    ButtonComponent,
    ToastComponent,
    SearchControlsComponent,
    FrozenColumnsSidebarComponent,
    FilterSidebarComponent,
    ActiveFiltersComponent,
    ExportConfirmationModalComponent,
    VersionHistoryModalComponent,
    PrioritizationModalComponent,
    MetricsDashboardComponent
  ],
  templateUrl: './traceability-list.component.html',
  styleUrls: ['./traceability-list.component.scss'],
  providers: [
    FrozenColumnsService,
    {
      provide: 'FROZEN_COLUMNS_STORAGE_KEY',
      useValue: 'sig-frozen-columns-traceability'
    }
  ]
})
export class TraceabilityListComponent implements OnInit, OnDestroy {
  @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;

  private readonly presenter = inject(TraceabilityPresenter);
  private readonly toastService = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  protected readonly viewExpansionState = inject(ViewExpansionStateService);
  private readonly frozenColumnsService = inject(FrozenColumnsService);

  private readonly _samples = signal<CaseListItem[]>([]);
  readonly samples = this._samples.asReadonly();

  private readonly _currentPage = signal(0);
  readonly currentPage = this._currentPage.asReadonly();

  private readonly _pageSize = signal(10);
  readonly pageSize = this._pageSize.asReadonly();

  private readonly _totalRecords = signal(0);
  readonly totalRecords = this._totalRecords.asReadonly();

  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();

  private dataSubscription?: Subscription;

  readonly ButtonTypeEnum = ButtonTypeEnum;
  readonly ButtonSizeEnum = ButtonSizeEnum;

  // Search controls
  private readonly _showSearchControls = signal(false);
  readonly showSearchControls = this._showSearchControls.asReadonly();
  readonly searchFieldOptions: SearchFieldOption[] =
    TRACEABILITY_SEARCH_OPTIONS;
  private currentSearchField: string | null = null;
  private currentSearchValue: string | null = null;

  // Frozen columns sidebar
  isFrozenSidebarOpen = false;

  // Filter sidebar
  isFilterSidebarOpen = false;
  currentFilterCriteria: FilterCriteria | null = null;
  hasActiveFilters = false;

  // History modal
  private readonly _isHistoryModalOpen = signal(false);
  readonly isHistoryModalOpen = this._isHistoryModalOpen.asReadonly();

  private readonly _selectedCase = signal<CaseListItem | null>(null);
  readonly selectedCase = this._selectedCase.asReadonly();

  private readonly _historyEntries = signal<VersionHistoryEntry[]>([]);
  readonly historyEntries = this._historyEntries.asReadonly();

  private readonly _isHistoryLoading = signal(false);
  readonly isHistoryLoading = this._isHistoryLoading.asReadonly();

  // Export modal
  private readonly _isExportModalOpen = signal(false);
  readonly isExportModalOpen = this._isExportModalOpen.asReadonly();

  private readonly _exportModalState = signal<ExportModalState>(
    ExportModalState.CONFIRMATION
  );
  readonly exportModalState = this._exportModalState.asReadonly();

  readonly ExportModalState = ExportModalState;

  // Prioritization modal
  private readonly _isPrioritizationModalOpen = signal(false);
  readonly isPrioritizationModalOpen = this._isPrioritizationModalOpen.asReadonly();

  private readonly _prioritizationCase = signal<CaseListItem | null>(null);
  readonly prioritizationCase = this._prioritizationCase.asReadonly();

  private readonly _isPrioritizationSaving = signal(false);
  readonly isPrioritizationSaving = this._isPrioritizationSaving.asReadonly();

  private metricsSubscription?: Subscription;

  readonly totalMetrics = signal<MetricItem[]>([]);
  readonly sampleStatusMetrics = signal<MetricItem[]>([]);
  readonly technologyMetrics = signal<MetricItem[]>([]);

  get isExpanded(): boolean {
    return this.viewExpansionState.isExpanded();
  }

  private readonly ICON_PRIORIDAD = 'icon-prioridad';
  private readonly ICON_EXAMEN = 'icon-examen-solicitado';
  private readonly ICON_CALENDARIO = 'icon-calendar';
  private readonly ICON_LAB = 'icon-jeringa';
  private readonly ICON_CODIGO = 'icon-recibo';
  private readonly ICON_CARNET = 'icon-carnet';
  private readonly ICON_USUARIO = 'icon-ficha-nombre';
  private readonly ICON_UBICACION = 'icon-ubicacion';
  private readonly ICON_MUESTRA = 'icon-muestra';
  private readonly ICON_DOCUMENTO = 'icon-documento';
  private readonly ICON_ESTADO = 'icon-estado';
  private readonly ICON_BOOLEAN_CHECK = 'pi pi-check-circle text-success';


  readonly ALL_COLUMNS = [
    { key: 'priority', label: 'Prioridad', searchable: false, icon: this.ICON_PRIORIDAD, visible: true, frozen: true, type: 'priority' as const },
    { key: 'examCode', label: 'Código Examen', searchable: false, icon: this.ICON_EXAMEN, visible: true, frozen: false },
    { key: 'examType', label: 'Tipo Examen', searchable: false, icon: this.ICON_EXAMEN, visible: true, frozen: false, type: 'examType' as const },
    { key: 'lgNumber', label: 'Código LG', searchable: true, icon: this.ICON_CODIGO, visible: true, frozen: false },
    { key: 'tubeCode', label: 'Código Tubo', searchable: false, icon: this.ICON_CODIGO, visible: true, frozen: false },
    { key: 'admissionCity', label: 'Ciudad Ingreso', searchable: false, icon: this.ICON_UBICACION, visible: true, frozen: false },
    { key: 'admissionDate', label: 'Fecha Ingreso', searchable: false, icon: this.ICON_CALENDARIO, visible: true, frozen: false, type: 'date' as const, tooltip: { pipe: 'fullDateTime' as const, alwaysShow: true } },

    { key: 'identificationType', label: 'Tipo ID', searchable: false, icon: this.ICON_CARNET, visible: true, frozen: false },
    { key: 'identificationNumber', label: 'Documento', searchable: true, icon: this.ICON_CARNET, visible: true, frozen: false },
    { key: 'patientName', label: 'Nombres', searchable: false, icon: this.ICON_USUARIO, visible: true, frozen: false },
    { key: 'patientLastName', label: 'Apellidos', searchable: false, icon: this.ICON_USUARIO, visible: true, frozen: false },
    { key: 'gender', label: 'Género', searchable: false, icon: this.ICON_USUARIO, visible: true, frozen: false },
    { key: 'birthDate', label: 'Fecha Nacimiento', searchable: false, icon: this.ICON_CALENDARIO, visible: true, frozen: false, type: 'date' as const },
    { key: 'birthCity', label: 'Ciudad Nacimiento', searchable: false, icon: this.ICON_UBICACION, visible: false, frozen: false },
    { key: 'birthDepartment', label: 'Departamento Nacimiento', searchable: false, icon: this.ICON_UBICACION, visible: false, frozen: false },
    { key: 'birthCountry', label: 'País Nacimiento', searchable: false, icon: this.ICON_UBICACION, visible: false, frozen: false },
    { key: 'city', label: 'Ciudad', searchable: false, icon: this.ICON_UBICACION, visible: false, frozen: false },

    { key: 'diagnosis', label: 'Diagnóstico', searchable: false, icon: this.ICON_DOCUMENTO, visible: true, frozen: false },
    { key: 'requestedExam', label: 'Examen Solicitado', searchable: false, icon: this.ICON_EXAMEN, visible: true, frozen: false },
    { key: 'acmg', label: 'ACMG', searchable: false, icon: this.ICON_EXAMEN, visible: true, frozen: false, type: 'boolean-icon' as const, booleanIcon: this.ICON_BOOLEAN_CHECK },
    { key: 'mlpa', label: 'MLPA', searchable: false, icon: this.ICON_EXAMEN, visible: true, frozen: false, type: 'boolean-icon' as const, booleanIcon: this.ICON_BOOLEAN_CHECK },
    { key: 'validationDate', label: 'Fecha Validación', searchable: false, icon: this.ICON_CALENDARIO, visible: true, frozen: false, type: 'date' as const },
    { key: 'observations', label: 'Observaciones', searchable: false, icon: this.ICON_DOCUMENTO, visible: true, frozen: false },
    { key: 'treatingPhysician', label: 'Médico Tratante', searchable: false, icon: this.ICON_USUARIO, visible: true, frozen: false },
    { key: 'sampleArrivalDate', label: 'Fecha Llegada Muestra', searchable: false, icon: this.ICON_CALENDARIO, visible: true, frozen: false, type: 'date' as const },
    { key: 'clinicalHistorySummary', label: 'Resumen Historia Clínica', searchable: false, icon: this.ICON_DOCUMENTO, visible: true, frozen: false },

    { key: 'mendelicsProductId', label: 'Producto Mendelics (Id)', searchable: false, icon: this.ICON_CODIGO, visible: true, frozen: false },
    { key: 'mendelicsPatientCreationDate', label: 'Fecha Creación Paciente Mendelics', searchable: false, icon: this.ICON_CALENDARIO, visible: true, frozen: false, type: 'date' as const },

    { key: 'age', label: 'Edad', searchable: false, icon: this.ICON_USUARIO, visible: true, frozen: false },
    { key: 'boxNumber', label: 'Número Caja', searchable: false, icon: this.ICON_MUESTRA, visible: true, frozen: false },
    { key: 'boxPosition', label: 'Posición en Caja', searchable: false, icon: this.ICON_MUESTRA, visible: true, frozen: false },
    { key: 'patientEmail', label: 'Correo Paciente', searchable: false, icon: this.ICON_USUARIO, visible: true, frozen: false },

    { key: 'status', label: 'Estado', searchable: false, icon: this.ICON_ESTADO, visible: true, frozen: false, type: 'status' as const },
    { key: 'outputDate', label: 'Promesa', searchable: false, icon: this.ICON_CALENDARIO, visible: true, frozen: false, type: 'date' as const },

    { key: 'caseNumber', label: 'Código Sura', searchable: false, icon: this.ICON_CODIGO, visible: true, frozen: false },
    { key: 'processingDate', label: 'Fecha Procesamiento', searchable: false, icon: this.ICON_CALENDARIO, visible: true, frozen: false, type: 'date' as const },
    { key: 'laboratory', label: 'Laboratorio de Referencia', searchable: false, icon: this.ICON_LAB, visible: true, frozen: false },
    { key: 'researchConsent', label: 'Consentimiento Investigación', searchable: false, icon: this.ICON_DOCUMENTO, visible: true, frozen: false, type: 'boolean-icon' as const, booleanIcon: this.ICON_BOOLEAN_CHECK },
    { key: 'sampleType', label: 'Tipo de Muestra', searchable: false, icon: this.ICON_MUESTRA, visible: true, frozen: false },

    { key: 'prioritizationRequestDate', label: 'Fecha Solicitud Priorización', searchable: false, icon: this.ICON_CALENDARIO, visible: true, frozen: false, type: 'date' as const },
    { key: 'prioritizationReason', label: 'Motivo Priorización', searchable: false, icon: this.ICON_DOCUMENTO, visible: true, frozen: false },
    { key: 'prioritization', label: 'Priorización', searchable: false, icon: this.ICON_PRIORIDAD, visible: true, frozen: false, type: 'boolean-icon' as const, booleanIcon: this.ICON_BOOLEAN_CHECK }
  ];

  readonly tableActions: TableActionType[] = [
    {
      icon: 'icon-documento',
      tooltip: 'Ver historial de trazabilidad',
      function: (sampleItemOrId: CaseListItem | string) => {
        if (typeof sampleItemOrId === 'string') {
          const sampleItem = this.samples().find(
            (s: CaseListItem) => s.caseNumber === sampleItemOrId
          );
          if (sampleItem) {
            this.openHistoryModal(sampleItem);
          }
        } else {
          this.openHistoryModal(sampleItemOrId);
        }
      },
      functionProperty: 'caseNumber'
    },
    {
      icon: 'icon-prioridad',
      tooltip: 'Registrar priorización',
      function: (sampleItemOrId: CaseListItem | string) => {
        if (typeof sampleItemOrId === 'string') {
          const sampleItem = this.samples().find(
            (s: CaseListItem) => s.caseNumber === sampleItemOrId
          );
          if (sampleItem) {
            this.openPrioritizationModal(sampleItem);
          }
        } else {
          this.openPrioritizationModal(sampleItemOrId);
        }
      },
      functionProperty: 'caseNumber'
    }
  ];

  readonly availableColumns: ColumnOption[] = this.ALL_COLUMNS.map((col) => ({
    key: col.key,
    label: col.label,
    frozen: col.frozen
  }));

  private readonly ALL_COLUMN_KEYS = this.ALL_COLUMNS.map((col) => col.key);

  readonly frozenColumnsInOrder = computed(() => {
    return this.frozenColumnsService.getFrozenColumnsInOrder(
      this.ALL_COLUMN_KEYS
    );
  });

  readonly visibleColumnsComputed = computed(() => {
    const frozenInOrder = this.frozenColumnsService.getFrozenColumnsInOrder(
      this.ALL_COLUMN_KEYS
    );

    const nonFrozenInOrder =
      this.frozenColumnsService.getNonFrozenColumnsInOrder(
        this.ALL_COLUMN_KEYS
      );

    const orderedKeys = [...frozenInOrder, ...nonFrozenInOrder];

    return orderedKeys
      .map((key) => this.ALL_COLUMNS.find((col) => col.key === key))
      .filter(
        (col): col is (typeof this.ALL_COLUMNS)[0] => col?.visible ?? false
      );
  });

  ngOnInit(): void {
    this.loadSamples();
    this.loadMetrics();
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
    this.metricsSubscription?.unsubscribe();
  }


  private loadMetrics(): void {
    this.metricsSubscription?.unsubscribe();

    const metricsParams = this.buildMetricsParamsFromFilters();

    this.metricsSubscription = this.presenter.getMetricsStream(metricsParams).subscribe({
      next: (metrics: TraceabilityMetrics) => {
        this.updateMetricsSections(metrics);
        this.cdr.markForCheck();
      },
      error: () => {
        this.updateMetricsSections(this.getEmptyMetrics());
      }
    });
  }


  private buildMetricsParamsFromFilters(): TraceabilityMetricsFilterParams | undefined {
    console.log('buildMetricsParamsFromFilters - currentFilterCriteria:', this.currentFilterCriteria);
    console.log('buildMetricsParamsFromFilters - hasActiveFilters:', this.hasActiveFilters);

    if (!this.currentFilterCriteria || !this.hasActiveFilters) {
      console.log('buildMetricsParamsFromFilters - returning undefined (no criteria or no active filters)');
      return undefined;
    }

    const facilityIds = this.currentFilterCriteria.facility && this.currentFilterCriteria.facility.length > 0
      ? this.currentFilterCriteria.facility.map((f) => f.code)
      : undefined;

    console.log('buildMetricsParamsFromFilters - facility:', this.currentFilterCriteria.facility);
    console.log('buildMetricsParamsFromFilters - facilityIds:', facilityIds);

    const statusIds = this.currentFilterCriteria.sampleStatus && this.currentFilterCriteria.sampleStatus.length > 0
      ? this.currentFilterCriteria.sampleStatus.map((s) => s.code)
      : undefined;

    const priorityIdToNameMap: Record<string, string> = {
      '1': 'ESTANDAR',
      '2': 'ADMINISTRATIVO',
      '3': 'INSTITUCION',
      '4': 'CLINICA'
    };

    const priorityIds = this.currentFilterCriteria.priority && this.currentFilterCriteria.priority.length > 0
      ? this.currentFilterCriteria.priority.map((id) => priorityIdToNameMap[id] || id)
      : undefined;

    console.log('buildMetricsParamsFromFilters - priority:', this.currentFilterCriteria.priority);
    console.log('buildMetricsParamsFromFilters - priorityIds:', priorityIds);
    console.log('buildMetricsParamsFromFilters - startDate raw:', this.currentFilterCriteria.startDate);
    console.log('buildMetricsParamsFromFilters - endDate raw:', this.currentFilterCriteria.endDate);

    const startDate = this.currentFilterCriteria.startDate
      ? this.formatDateForBackend(this.currentFilterCriteria.startDate)
      : undefined;

    const endDate = this.currentFilterCriteria.endDate
      ? this.formatDateForBackend(this.currentFilterCriteria.endDate)
      : undefined;

    console.log('buildMetricsParamsFromFilters - startDate formatted:', startDate);
    console.log('buildMetricsParamsFromFilters - endDate formatted:', endDate);


    const hasApplicableFilters = facilityIds || statusIds || priorityIds || startDate || endDate;
    console.log('buildMetricsParamsFromFilters - hasApplicableFilters:', hasApplicableFilters);

    if (!hasApplicableFilters) {
      console.log('buildMetricsParamsFromFilters - returning undefined (no applicable filters)');
      return undefined;
    }

    const params = { facilityIds, statusIds, priorityIds, startDate, endDate };
    console.log('buildMetricsParamsFromFilters - returning params:', params);
    return params;
  }

  private formatDateForBackend(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private updateMetricsSections(metrics: TraceabilityMetrics): void {
    const today = new Date().toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

    // Totals section
    this.totalMetrics.set([
      { label: 'Hoy', value: today, isDate: true, icon: 'icon-calendar' },
      { label: 'Total general', value: metrics.totalGeneral },
      { label: 'Total del día', value: metrics.totalDelDia }
    ]);

    // Sample status section
    this.sampleStatusMetrics.set([
      { label: 'Pend. Extracción', value: metrics.pendienteExtraccion },
      { label: 'En Proceso', value: metrics.enProceso },
      { label: 'Extracción Completada', value: metrics.extraccionCompletada },
      { label: 'En Librería', value: metrics.enLibreria },
      { label: 'En Corrida', value: metrics.enCorrida }
    ]);

    // Technology section
    this.technologyMetrics.set([
      { label: 'NGS', value: metrics.ngs },
      { label: 'Microarreglos', value: metrics.microarreglos },
      { label: 'MLPA', value: metrics.mlpa },
      { label: 'Expansión Tripletas', value: metrics.expansionTripletas }
    ]);
  }

  private getEmptyMetrics(): TraceabilityMetrics {
    return {
      totalGeneral: 0,
      totalDelDia: 0,
      pendienteExtraccion: 0,
      enProceso: 0,
      extraccionCompletada: 0,
      enLibreria: 0,
      enCorrida: 0,
      ngs: 0,
      microarreglos: 0,
      mlpa: 0,
      expansionTripletas: 0
    };
  }

  loadSamples(): void {
    this._isLoading.set(true);

    const params = {
      page: this._currentPage(),
      size: this._pageSize(),
      searchQuery: this.currentSearchValue || undefined,
      searchField: this.currentSearchField || undefined,
      filters: this.currentFilterCriteria
    };

    this.dataSubscription?.unsubscribe();
    this.dataSubscription = this.presenter.getSamples(params).subscribe({
      next: (result) => {
        this._samples.set(result.samples);
        this._totalRecords.set(result.pagination.total);
        this._isLoading.set(false);
        this.cdr.markForCheck();
      },
      error: () => {
        // Error is handled by interceptor - just reset local state
        this.toastService.setToast({
          title: 'Error',
          message: 'Error al cargar las muestras',
          type: ToastTypeEnum.ERROR
        });
        this._isLoading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  onPageChange(pagination: { page: number; pageSize: number }): void {
    this._currentPage.set(pagination.page);
    this._pageSize.set(pagination.pageSize);
    this.loadSamples();
  }

  // Search controls
  toggleSearchControls(): void {
    this._showSearchControls.set(!this._showSearchControls());
  }

  handleSearch(event: SearchEvent): void {
    this.currentSearchField = event.searchField;
    this.currentSearchValue = event.searchValue;
    this._currentPage.set(0);
    this.loadSamples();
  }

  handleClearSearch(): void {
    this.currentSearchField = null;
    this.currentSearchValue = null;
    this._currentPage.set(0);
    this.loadSamples();
  }

  // Filter sidebar
  toggleFilterSidebar(): void {
    this.isFilterSidebarOpen = !this.isFilterSidebarOpen;
  }

  closeFilterSidebar(): void {
    this.isFilterSidebarOpen = false;
  }

  handleApplyFilters(filters: FilterCriteria): void {
    console.log('handleApplyFilters called with:', filters);
    this.currentFilterCriteria = filters;
    this.hasActiveFilters = filters !== null && Object.values(filters).some(v => v !== null && v !== undefined && (Array.isArray(v) ? v.length > 0 : true));
    console.log('hasActiveFilters:', this.hasActiveFilters);
    this._currentPage.set(0);
    this.loadSamples();
    this.loadMetrics(); // Reload metrics with filters
    this.closeFilterSidebar();
  }

  clearFilters(): void {
    this.currentFilterCriteria = {};
    this.hasActiveFilters = false;
    this._currentPage.set(0);
    this.loadSamples();
    this.loadMetrics(); // Reload metrics without filters
  }

  getFilterOptionsService(): IFilterOptionsQueryPort {
    return {
      getFilterOptions: () => of({
        causes: [],
        statuses: [],
        priorities: [],
        examTypes: [],
        regions: [],
        sites: [],
        sampleStatuses: [],
        sampleTypes: [],
        facilities: [
          { code: 'Ay Dx IPS Sura Saman', name: 'Ay Dx IPS Sura Saman' }
        ],
        reprocessCauses: []
      })
    };
  }

  // Frozen columns sidebar
  toggleFrozenColumnsSidebar(): void {
    this.isFrozenSidebarOpen = !this.isFrozenSidebarOpen;
  }

  closeFrozenColumnsSidebar(): void {
    this.isFrozenSidebarOpen = false;
  }

  // Expansion
  toggleExpansion(): void {
    this.viewExpansionState.toggle();
  }

  // History modal
  private openHistoryModal(sampleItem: CaseListItem): void {
    this._selectedCase.set(sampleItem);
    this._isHistoryModalOpen.set(true);
    this._isHistoryLoading.set(true);

    const identifier = sampleItem.lgNumber || sampleItem.caseNumber;

    this.presenter.getSampleHistory(identifier).subscribe({
      next: (history: VersionHistoryEntry[]) => {
        this._historyEntries.set(history);
        this._isHistoryLoading.set(false);
      },
      error: () => {
        this.toastService.setToast({
          title: 'Error',
          message: 'Error al cargar el historial de trazabilidad',
          type: ToastTypeEnum.ERROR
        });
        this._historyEntries.set([]);
        this._isHistoryLoading.set(false);
      }
    });
  }

  closeHistoryModal(): void {
    this._isHistoryModalOpen.set(false);
    this._selectedCase.set(null);
    this._historyEntries.set([]);
  }

  // Prioritization modal
  private openPrioritizationModal(sampleItem: CaseListItem): void {
    this._prioritizationCase.set(sampleItem);
    this._isPrioritizationModalOpen.set(true);
  }

  closePrioritizationModal(): void {
    this._isPrioritizationModalOpen.set(false);
    this._prioritizationCase.set(null);
    this._isPrioritizationSaving.set(false);
  }

  handleSavePrioritization(result: PrioritizationResult): void {
    const caseItem = this._prioritizationCase();
    if (!caseItem?.lgNumber) {
      return;
    }

    this._isPrioritizationSaving.set(true);

    this.presenter
      .updatePrioritization(caseItem.lgNumber, {
        prioritization: result.prioritization,
        reason: result.prioritizationReason
      })
      .subscribe({
        next: () => {
          this.toastService.setToast({
            title: 'Priorización actualizada',
            message: 'La priorización se registró correctamente',
            type: ToastTypeEnum.SUCCESS
          });
          this.closePrioritizationModal();
          this.loadSamples();
        },
        error: () => {
          this.toastService.setToast({
            title: 'Error',
            message: 'No se pudo actualizar la priorización. Intente nuevamente.',
            type: ToastTypeEnum.ERROR
          });
          this._isPrioritizationSaving.set(false);
        }
      });
  }

  // Export methods
  openExportModal(): void {
    this._exportModalState.set(ExportModalState.CONFIRMATION);
    this._isExportModalOpen.set(true);
  }

  closeExportModal(): void {
    this._isExportModalOpen.set(false);
    setTimeout(() => {
      this._exportModalState.set(ExportModalState.CONFIRMATION);
    }, 300);
  }

  handleExportConfirm(): void {
    this._exportModalState.set(ExportModalState.DOWNLOADING);

    // Build current filter params
    const params = this.buildCurrentQueryParams();

    this.presenter.exportSamplesToCSV(params).subscribe({
      next: (blob: Blob) => {
        this.downloadCsvFile(blob);
        this.closeExportModal();
        this.toastService.setToast({
          title: 'Exportación exitosa',
          message: 'El archivo CSV se ha descargado correctamente',
          type: ToastTypeEnum.SUCCESS
        });
      },
      error: () => {
        this.closeExportModal();
        this.toastService.setToast({
          title: 'Error de exportación',
          message: 'No se pudo generar el archivo CSV. Intente nuevamente.',
          type: ToastTypeEnum.ERROR
        });
      }
    });
  }

  private downloadCsvFile(blob: Blob): void {
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '')
      .slice(0, 15);
    const filename = `trazabilidad_muestras_${timestamp}.csv`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  private buildCurrentQueryParams(): any {
    return {
      page: this._currentPage(),
      size: this._pageSize(),
      searchQuery: this.currentSearchValue,
      searchField: this.currentSearchField,
      priority: this.currentFilterCriteria?.priority?.[0],
      status: this.currentFilterCriteria?.sampleStatus?.[0]?.code,
      examType: this.currentFilterCriteria?.examType?.map(e => e.code).join(','),
      sampleType: this.currentFilterCriteria?.sampleType?.map(s => s.code).join(','),
      facility: this.currentFilterCriteria?.facility?.map(f => f.code).join(','),
      dateFrom: this.currentFilterCriteria?.startDate,
      dateTo: this.currentFilterCriteria?.endDate
    };
  }
}
